<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'

const COPY_FEEDBACK_MS = 1600

// One label / value row with its own copy button — the escrow bank details are
// transcribed into a banking app field by field, so each line copies on its own
// rather than forcing the investor to select text out of a block.
interface Props {
  label: string
  value: string
  /** Value actually written to the clipboard when it differs from the display
   *  form (e.g. an IBAN shown in spaced groups). */
  copyValue?: string
}

const props = defineProps<Props>()

const copied = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
})

const onCopy = async () => {
  try {
    await navigator.clipboard.writeText(props.copyValue ?? props.value)
    copied.value = true
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      copied.value = false
    }, COPY_FEEDBACK_MS)
  } catch {
    // clipboard unavailable — the value stays selectable on screen
  }
}
</script>

<template>
  <Box align="center" gap="4" class="cdr">
    <Box direction="col" gap="1" grow class="cdr__text">
      <Text type="body" color="muted" is="span">{{ label }}</Text>
      <Text type="heading" weight="medium" is="span" class="cdr__value">{{ value }}</Text>
    </Box>

    <Box
      is="button"
      flex
      align="center"
      justify="center"
      class="cdr__copy"
      type="button"
      :aria-label="copied ? `${label} copied` : `Copy ${label}`"
      :title="copied ? 'Copied' : 'Copy'"
      @click="onCopy"
    >
      <PiIcon :name="copied ? 'check' : 'copy'" class="cdr__copy-icon" />
    </Box>
  </Box>
</template>

<style scoped>
.cdr {
  padding: 0.75rem 0;
}

.cdr__text {
  min-width: 0;
}

.cdr__value {
  word-break: break-word;
}

.cdr__copy {
  width: 30px;
  height: 26px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-300);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
  color: var(--p-text-muted-color);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.cdr__copy:hover {
  background: var(--p-primary-50);
  border-color: var(--p-primary-200);
  color: var(--p-primary-600);
}

.cdr__copy:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.cdr__copy-icon {
  font-size: 12px;
}
</style>
